// frontend/src/pages/MasterClerkDashboard.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { getUserFromToken } from "../utils/auth";
import socket, { connectSocketWithToken } from "./socket";
import "../styles/MasterClerkDashboard.css";
import "../styles/dashboard.css"; // Wave 2B: shared operational dashboard layer
import api from "../utils/api";
import staticHierarchySeed from "../utils/hierarchy"; // F1: legacy seed fallback only
import { useStructureSnapshot } from "../utils/structureSnapshot"; // F1: DB-backed structure snapshot
import FileCard from "./FileCard";
import { fetchCurrentOfficeRecipients, fetchClerkRecipients, getClerkRecipients, fetchSoiRecipients } from "../utils/getNextRecipient"; // H1.2: DB-backed clerk network & SOI
import { resolveHierarchyDestination } from "../utils/destinationScope";
import { filterClerkRecipientOptions, toRecipientPayload } from "../utils/forwardRecipients"; // forwarding payload SSOT (INTERNAL / EXTERNAL / CLERK_NETWORK)
import { getApiBaseUrl } from "../utils/baseUrl";
import { fetchFileBlob } from "../utils/secureFileAccess";
import { getRoleIconMeta } from "../utils/roleIcons";
import { getDisplayFileName } from "../utils/fileName";
import DashboardViewTabs from "../components/dashboard/DashboardViewTabs"; // Wave 2B shared view tabs
import DashboardSummary from "../components/dashboard/DashboardSummary"; // Wave 2B shared KPI row
import { PageHeader, Button as WavyButton, StatusBadge, EmptyState, Skeleton, TableShell, Modal, FormField, Input, Select } from "../components/ui";
import CreateDocumentMenu from "./DocumentStudio/CreateDocumentMenu";
import {
  Archive,
  BookOpen,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Compass,
  Crown,
  Database,
  FileSearch,
  GraduationCap,
  Inbox,
  Landmark,
  Map as MapIcon,
  Medal,
  Monitor,
  Radio,
  Send,
  Settings,
  Shield,
  Target,
  Truck,
  User,
  Users,
  Wrench,
} from "lucide-react";
const API_URL = getApiBaseUrl();

// Define classifications
const classifications = ["Restricted", "Confidential", "Secret"];

const searchOptions = [
  { label: "File Name", value: "fileName" },
  { label: "Status", value: "status" },
  { label: "Classification", value: "classification" },
  { label: "Uploaded By (Role)", value: "uploadedBy" },
  { label: "Recipient", value: "recipient" },
];

const ACTIONED_STATUS_SET = new Set(["actioned", "reviewed", "approved", "finalapproved"]);
const ACTIONED_HISTORY_SET = new Set(["approve", "review", "archive", "actioned", "reviewed", "finalapproved"]);

const ROLE_ICON_COMPONENTS = {
  archive: Archive,
  barChart: Archive,
  bookOpen: BookOpen,
  briefcase: Briefcase,
  clipboard: ClipboardList,
  compass: Compass,
  crown: Crown,
  database: Database,
  fileSearch: FileSearch,
  fileText: FileSearch,
  graduationCap: GraduationCap,
  inbox: Inbox,
  keyboard: ClipboardList,
  landmark: Landmark,
  map: MapIcon,
  medal: Medal,
  monitor: Monitor,
  radio: Radio,
  send: Send,
  settings: Settings,
  shield: Shield,
  target: Target,
  truck: Truck,
  user: User,
  users: Users,
  wrench: Wrench,
};
const MasterClerkDashboard = () => {
  // ===================== STATE =====================
  const [view, setView] = useState("inbox"); // inbox | sent | archive

  const [files, setFiles] = useState([]); // "Sent" (uploaded by me)
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [classification, setClassification] = useState("");
  const [filePassword, setFilePassword] = useState("");
  const [route, setRoute] = useState([]);
  const [message, setMessage] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadMessageType, setUploadMessageType] = useState("info");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isCreateDocMenuOpen, setIsCreateDocMenuOpen] = useState(false);

  // Inbox (Received)
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [inboxFiles, setInboxFiles] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [processingFileId, setProcessingFileId] = useState(null);

  // Archive
  const [archiveSearchTerm, setArchiveSearchTerm] = useState("");
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [archivePage, setArchivePage] = useState(1);
  const [archiveTotalPages, setArchiveTotalPages] = useState(1);

  // Modals
  const [selectedFileForModal, setSelectedFileForModal] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);

  // Password view modal
  const [currentFile, setCurrentFile] = useState(null);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Recipients UI
  const [showRecipients, setShowRecipients] = useState(true);
  const [selectedClerkFormation, setSelectedClerkFormation] = useState("");
  const [selectedClerkUnit, setSelectedClerkUnit] = useState("");

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("fileName");

  // ===================== HELPERS =====================
  const normalize = (s) => (s || "").toString().trim().toUpperCase();
  const normalizeLoose = (s) => normalize(s).replace(/[^A-Z0-9]/g, "");

  // ===================== USER CONTEXT =====================
  // F1: the DB structure snapshot (registration-structure, cached and shared
  // across pages) is the authority for the hierarchy browser / formation lists;
  // the static seed import is only the offline fallback passed into the hook.
  const hierarchy = useStructureSnapshot(staticHierarchySeed);
  const token = localStorage.getItem("authToken");
  const user = getUserFromToken();
  const username = user?.username;
  const role = user?.role;
  const branch = user?.branch;
  const subBranch = user?.subBranch;
  const unitBranch = user?.unitBranch;
  // H1.2 fix: normalized user unit + SOI flag used by the authorities loader.
  const unitNorm = normalize(user?.unit || user?.unitBranch || "KA");
  const isSOI = unitNorm === "SOI";

  const roleNorm = normalize(role);
  const branchNormSafe = normalize(branch);
  const subBranchNormSafe = normalize(subBranch);

  const clerkTheme = useMemo(() => {
    if (roleNorm === "CHIEF CLERK") return "chief";
    if (roleNorm === "DORCUS CLERK") return "dorcus";
    if (roleNorm === "COMCEN") return "comcen";
    if (subBranchNormSafe.includes("CIS")) return "cis";
    if (subBranchNormSafe.includes("INT")) return "intel";
    if (subBranchNormSafe.includes("OOTW")) return "ootw";
    if (subBranchNormSafe.includes("SYS")) return "sys";
    if (subBranchNormSafe.includes("OPS")) return "ops";
    if (branchNormSafe.includes("SOI")) return "soi";
    return "default";
  }, [roleNorm, subBranchNormSafe, branchNormSafe]);

  const clerkTitle = useMemo(() => {
    if (roleNorm === "CHIEF CLERK") return "Chief Clerk Command Desk";
    if (roleNorm === "DORCUS CLERK") return "Dorcus Clerk Operations Desk";
    if (roleNorm === "COMCEN") return "Comcen Traffic Desk";
    return "Clerk Workflow Dashboard";
  }, [roleNorm]);

  const { Icon: RoleIcon, label: roleIconLabel } = useMemo(() => {
    const meta = getRoleIconMeta(role || "Clerk");
    return {
      Icon: ROLE_ICON_COMPONENTS[meta.key] || User,
      label: meta.label,
    };
  }, [role]);

  const formatTimestamp = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "N/A";
    }
  };

  const getUploadedByRole = (file) => {
    const uploadedRole = String(file?.uploadedByRole || "").trim();
    if (uploadedRole) return uploadedRole;
    return "Unknown Role";
  };

  const getSearchValue = (file, field) => {
    if (field === "recipient") {
      return (
        file.currentLevel ||
        (Array.isArray(file.recipients) ? file.recipients.join(", ") : "")
      );
    }
    if (field === "uploadedBy") {
      return getUploadedByRole(file);
    }
    if (field === "fileName") {
      return getDisplayFileName(file);
    }
    return file[field] || "";
  };

  // ================= NORMALIZATION MAPS =================
  const BRANCH_MAP = {
    "MAIN HQ": "HQ ASOB",
    "HQ ASOB": "HQ ASOB",
    ASOB: "HQ ASOB",

    "HQ 20 PARA BN": "HQ 20 Para BN",
    "HQ 20 PARABN": "HQ 20 Para BN",
    "HQ 60 PARA BN": "HQ 60 ASB",
    "HQ 60 ASB": "HQ 60 ASB",
    "HQ 40 RANGER": "HQ 40 Ranger",
    "HQ SQTS": "HQ SQTS",

    SIGNAL: "KAHAWA GARRISON",
    "SIGNAL BRANCH": "KAHAWA GARRISON",
    "KAHAWA GARRISON - SIG BN": "KAHAWA GARRISON",
    "AHQ SIG BN": "KAHAWA GARRISON",
  };

  const SUB_BRANCH_MAP = {
    CLERKS: "MAIN HQ",
    "MAIN HQ": "MAIN HQ",
    "HQ ASOB / MAIN HQ": "MAIN HQ",
    BATTALION: "BATTALION",
    BATALLION: "BATTALION",
    "BATTALION HQ": "BATTALION",
    SOS: "SOS",
    "SOS BRANCH": "SOS",
    "SIGNAL SOS": "SOS",
    KACS: "KACS",
    "AHQ SIG BN": "AHQ SIG BN",
    "IT SQDRN": "IT SQDRN",
    "COMMS SQDRN": "COMMS SQDRN",
  };

  const findHierarchyKey = (obj, wantedKey) => {
    const wanted = normalize(wantedKey);
    const wantedLoose = normalizeLoose(wantedKey);
    return (
      Object.keys(obj).find((k) => normalize(k) === wanted || normalizeLoose(k) === wantedLoose) ||
      null
    );
  };

  const findNestedHierarchyNode = (obj, wantedKey) => {
    if (!obj || typeof obj !== "object") return null;

    const directKey = findHierarchyKey(obj, wantedKey);
    if (directKey) return obj[directKey];

    for (const value of Object.values(obj)) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const found = findNestedHierarchyNode(value, wantedKey);
        if (found) return found;
      }
    }

    return null;
  };

  const findParentBranchForSubBranch = (wantedKey) => {
    const wanted = normalize(wantedKey);
    const wantedLoose = normalizeLoose(wantedKey);
    if (!wanted && !wantedLoose) return null;

    return (
      Object.keys(hierarchy).find((topLevelKey) => {
        const subBranches = hierarchy?.[topLevelKey]?.subBranches;
        return !!findNestedHierarchyNode(subBranches, wanted) || !!findNestedHierarchyNode(subBranches, wantedLoose);
      }) || null
    );
  };

  const flattenRoles = (node) => {
    if (Array.isArray(node)) return node;
    if (!node || typeof node !== "object") return [];
    return Object.values(node).flatMap(flattenRoles);
  };

  // H1.2: clerk-network recipients come from the DB routing service, not a
  // static per-unit map. The display-only clerk desk pattern (used to
  // highlight clerk desks in the hierarchy browser) matches the backend
  // CLERK_DESK_PATTERN semantics.
  const clerkPattern = /(clerk|comcen|registry|dispatch|typing|archive|dorcus|master)/i;

  const getClerkRecipientsFromNode = (node, nodeKey = "") => {
    if (!node) return [];
    // Display-only fallback; the authoritative list is DB-backed via
    // fetchClerkRecipients (see selectedClerkRecipients). The backend
    // re-validates every submission server-side.
    return Array.from(
      new Set(flattenRoles(node).filter((roleName) => clerkPattern.test(String(roleName || ""))))
    ).sort();
  };

  const collectClerkDeskKeys = (node) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return [];

    const deskKeys = [];

    Object.entries(node).forEach(([key, value]) => {
      const hasClerkRole =
        Array.isArray(value) && value.some((roleName) => clerkPattern.test(String(roleName || "")));

      if (hasClerkRole) deskKeys.push(key);
      if (value && typeof value === "object" && !Array.isArray(value)) {
        deskKeys.push(...collectClerkDeskKeys(value));
      }
    });

    return Array.from(new Set(deskKeys));
  };

  const findHierarchyPath = (node, wantedKey, path = []) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return null;
    for (const [key, value] of Object.entries(node)) {
      const nextPath = [...path, key];
      if (normalizeLoose(key) === normalizeLoose(wantedKey)) return nextPath;
      const nested = findHierarchyPath(value, wantedKey, nextPath);
      if (nested) return nested;
    }
    return null;
  };

  const branchKeyRaw = BRANCH_MAP[normalize(branch)] || branch;
  const subBranchKeyRaw = SUB_BRANCH_MAP[normalize(subBranch)] || subBranch;
  const inferredBranchFromBranch = findParentBranchForSubBranch(branchKeyRaw);
  const inferredBranchFromSubBranch = findParentBranchForSubBranch(subBranchKeyRaw);
  const branchKeyFound =
    findHierarchyKey(hierarchy, branchKeyRaw) ||
    inferredBranchFromBranch ||
    inferredBranchFromSubBranch;

  // ===================== AUTHORITIES (RECIPIENTS) — H1.2 DB-backed =====================
  // Normal Forward To and file actions use only the clerk's current scope.
  // Cross-unit desks remain in the separate Clerk Network picker.
  const [authorities, setAuthorities] = useState([]);
  const [officeRoster, setOfficeRoster] = useState(null);
  const currentOfficeScope = useMemo(() => isSOI ? {
    unit: user?.unit || "SOI", unitBranch: unitBranch || "", branch: branch || "", subBranch: subBranch || "",
  } : officeRoster ? ({
    ...officeRoster.scope, routingMode: "CURRENT_OFFICE",
    destinationOfficeId: officeRoster.officeId,
    routingAssignmentId: officeRoster.assignmentId,
    appointments: officeRoster.appointments,
  }) : null, [officeRoster, isSOI, user?.unit, unitBranch, branch, subBranch]);
  const [authoritiesLoading, setAuthoritiesLoading] = useState(false);
  const [authoritiesError, setAuthoritiesError] = useState("");
  const [authoritiesAttempt, setAuthoritiesAttempt] = useState(0);
  const [allClerkNetworkRecipientsVersion, setAllClerkNetworkRecipientsVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadAuthorities = async () => {
      if (isSOI) {
        const resolved = await fetchSoiRecipients();
        if (!cancelled) setAuthorities(filterClerkRecipientOptions(resolved));
        return;
      }
      const roster = await fetchCurrentOfficeRecipients();
      if (!cancelled) {
        setOfficeRoster(roster);
        setAuthorities(filterClerkRecipientOptions(roster.recipients));
      }
    };
    setOfficeRoster(null);
    // A refreshed office roster invalidates only normal Forward To selections.
    setRoute((current) => current.filter((entry) => entry.scope?.routingMode !== "CURRENT_OFFICE"));
    setAuthorities([]);
    setAuthoritiesLoading(true);
    setAuthoritiesError("");
    loadAuthorities().catch((error) => {
      if (!cancelled) setAuthoritiesError(error.message || "Could not load recipients. Please retry.");
    }).finally(() => {
      if (!cancelled) setAuthoritiesLoading(false);
    });
    return () => { cancelled = true; };
  }, [isSOI, unitNorm, branchKeyRaw, branch, subBranch, unitBranch, subBranchKeyRaw, authoritiesAttempt]);

  // ===================== LOGGING =====================
  useEffect(() => {
    console.log("🔍 [Clerk Dashboard] Mounted");
    console.log("👤 User:", { username, role, branch, subBranch, unit: user?.unit });
    console.log("🔑 Token exists:", !!token);
  }, [token, username, role, branch, subBranch, user?.unit]);

  // ===================== DATA FETCHERS =====================
  const fetchSentFiles = useCallback(
    async (u, page = 1, limit = 200) => {
      const sentBy = Array.from(
        new Set(
          [
            u,
            user?.username,
            user?.name,
            user?.serviceNumber,
            localStorage.getItem("username"),
            localStorage.getItem("name"),
          ].filter(Boolean)
        )
      );
      console.log("📤 [SENT] sentBy candidates:", sentBy, {
        username: user?.username,
        name: user?.name,
        serviceNumber: user?.serviceNumber,
      });
      if (sentBy.length === 0) {
        setMessage("⚠️ Please log in again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setMessage("");

      try {
        const res = await api.get("/api/uploads/files", {
          // Sent view should not be limited to Pending only.
          params: { view: "sent", page, limit, _t: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });

        const filesList = Array.isArray(res.data.files) ? res.data.files : res.data.files || [];
        console.log("📤 [SENT] response:", {
          count: filesList.length,
          total: res.data?.total,
          page: res.data?.page,
          pages: res.data?.pages,
        });
        setFiles(filesList);
        setMessage(filesList.length === 0 ? "ℹ️ No sent files found." : "");
      } catch (err) {
        console.error("❌ [SENT] Fetch failed:", err?.response?.data || err);
        setMessage("❌ Failed to load sent files.");
        setFiles([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchArchivedFiles = useCallback(
    async (page = 1, limit = 10) => {
      try {
        const [archivedRes, historyRes] = await Promise.all([
          api.get("/api/uploads/files", {
            params: { status: "Archived", page, limit, _t: Date.now() },
          }),
          api.get("/api/uploads/files", {
            params: { view: "history", page, limit, _t: Date.now() },
          }),
        ]);

        const archivedFiles = Array.isArray(archivedRes.data?.files) ? archivedRes.data.files : [];
        const historyFiles = Array.isArray(historyRes.data?.files) ? historyRes.data.files : [];
        const merged = [...archivedFiles, ...historyFiles];

        const deduped = Array.from(
          new Map(merged.map((f) => [String(f?._id || ""), f])).values()
        ).filter((f) => String(f?._id || "").trim());

        setArchiveFiles(deduped);
        setArchiveTotalPages(Math.max(archivedRes.data?.pages || 1, historyRes.data?.pages || 1));
        setArchivePage(page);
      } catch (err) {
        console.error("❌ [ARCHIVE] Fetch failed:", err?.response?.data || err);
        setArchiveFiles([]);
      }
    },
    []
  );

  const fetchInbox = useCallback(async () => {
    setLoadingInbox(true);
    try {
      // Backend does not expose /api/inbox; use routed uploads endpoint for inbox.
      const res = await api.get("/api/uploads/files", {
        params: {
          status: "Pending,Resubmission Required,Resubmitted",
          role: role || "",
          page: 1,
          limit: 200,
          _t: Date.now(),
        },
        headers: { "Cache-Control": "no-cache" },
      });

      const currentRole = normalize(role);
      const currentInboxFiles = (Array.isArray(res.data.files) ? res.data.files : []).filter(
        (item) => normalize(item?.currentLevel) === currentRole
      );
      setInboxFiles(currentInboxFiles);
      setInboxMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
    } catch (err) {
      console.error("Failed to fetch inbox", err);
      setInboxFiles([]);
      setInboxMessages([]);
    } finally {
      setLoadingInbox(false);
    }
  }, [role]);

  const handleInboxFileAction = async (
    fileId,
    action,
    comment = "",
    attachments = [],
    nextRecipient = "",
    userSignature = "",
    signaturePosition = { x: 0, y: 0 },
    signatureType = "signature",
    signItems = [],
    actionOptions = {}
  ) => {
    if (!token || processingFileId) return;
    setProcessingFileId(fileId);
    setMessage("");
    try {
      const normalizedAction = String(action || "").trim().toLowerCase();
      const formData = new FormData();
      formData.append("action", normalizedAction);
      formData.append("comment", comment || "");
      formData.append("reviewedBy", role || "CLERK");
      formData.append("reviewedByUserId", user?.userId || "");
      formData.append("reviewedByRole", role || "CLERK");

      if (normalizedAction === "approve" && nextRecipient) {
        formData.append("nextRecipient", nextRecipient);
        if (actionOptions.routingMode === "CURRENT_OFFICE") {
          for (const key of ["routingMode", "destinationOfficeId", "routingAssignmentId", "destinationAppointmentId"]) {
            formData.append(key, actionOptions[key] || "");
          }
        }
        if (actionOptions.destinationUnit) formData.append("destinationUnit", actionOptions.destinationUnit);
        if (actionOptions.destinationUnitBranch) formData.append("destinationUnitBranch", actionOptions.destinationUnitBranch);
        if (actionOptions.destinationBranch) formData.append("destinationBranch", actionOptions.destinationBranch);
        if (actionOptions.destinationSubBranch) formData.append("destinationSubBranch", actionOptions.destinationSubBranch);
      }
      if (userSignature) {
        formData.append("userSignature", JSON.stringify({
          userId: user?.userId || username || "",
          role: role || "CLERK",
          type: signatureType,
          image: userSignature,
        }));
        formData.append("signaturePosition", JSON.stringify(signaturePosition));
        formData.append("signatureType", signatureType);
      }
      if (Array.isArray(signItems) && signItems.length > 0) {
        formData.append("signItems", JSON.stringify(signItems));
      }
      (attachments || []).forEach((attachment) => formData.append("attachments", attachment));

      const response = await fetch(`${API_URL}/api/uploads/files/${fileId}/review`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "File forwarding failed.");

      setInboxFiles((current) => current.filter((item) => item._id !== fileId));
      setShowFileModal(false);
      setSelectedFileForModal(null);
      setMessage(data.message || (nextRecipient
        ? `File sent to ${nextRecipient} and cleared from the inbox.`
        : "File action completed and cleared from the inbox."));
      await Promise.all([fetchInbox(), fetchSentFiles(username, 1, 200)]);
    } catch (error) {
      console.error("Clerk inbox action failed", error);
      setMessage(error.message || "File forwarding failed.");
    } finally {
      setProcessingFileId(null);
    }
  };

  // ===================== INITIAL LOAD + TAB LOAD =====================
  useEffect(() => {
    if (!token) {
      setMessage("No authentication token found. Redirecting...");
      setTimeout(() => (window.location.href = "/login"), 1500);
      return;
    }

    // Clear stale view messages on tab switch
    setMessage("");

    // Gmail-like: load active tab
    if (view === "inbox") fetchInbox();
    if (view === "sent") fetchSentFiles(username, 1, 200);
    if (view === "archive") fetchArchivedFiles(1, 10);
  }, [token, view, username, fetchInbox, fetchSentFiles, fetchArchivedFiles]);

  // ===================== SOCKET REFRESH =====================
  useEffect(() => {
    if (!token) return;
    connectSocketWithToken(token);

    const refreshAll = () => {
      console.log("🔔 [SOCKET] Refreshing current tab:", view);
      if (view === "inbox") fetchInbox();
      if (view === "sent") fetchSentFiles(username, 1, 200);
      if (view === "archive") fetchArchivedFiles(1, 10);
    };

    socket.on("fileUploaded", refreshAll);
    socket.on("fileReviewed", refreshAll);
    socket.on("fileForwarded", refreshAll);
    socket.on("fileArchived", refreshAll);
    socket.on("fileDeleted", refreshAll);
    socket.on("fileResubmitted", refreshAll);

    socket.on("fileResubmissionRequired", (data) => {
      refreshAll();
      alert(`${data.message}${data.reason ? "\nReason: " + data.reason : ""}`);
    });

    return () => {
      socket.off("fileUploaded", refreshAll);
      socket.off("fileReviewed", refreshAll);
      socket.off("fileForwarded", refreshAll);
      socket.off("fileArchived", refreshAll);
      socket.off("fileResubmitted", refreshAll);
      socket.off("fileResubmissionRequired");
    };
  }, [token, view, username, fetchInbox, fetchSentFiles, fetchArchivedFiles]);

  // ===================== ACTIONS =====================
  // H1.2: the clerk-network recipient universe is DB-backed. It is the union
  // of the clerk desks resolvable through the routing service for the known
  // formations, seeded from the displayed hierarchy (which itself is the
  // registration-structure DB snapshot). No static map any more.

  // Sync clerk-network recipient caches for every formation on mount / when
  // the hierarchy snapshot changes (backend GET /api/users/routing/
  // clerk-recipients per formation).
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      for (const formation of Object.keys(hierarchy)) {
        if (formation === "Others") continue;
        await fetchClerkRecipients(formation, "", "");
        if (cancelled) return;
      }
      setAllClerkNetworkRecipientsVersion((v) => v + 1);
    };
    sync();
    return () => { cancelled = true; };
  }, [hierarchy]);

  const clerkNetworkFormations = useMemo(() => {
    const allFormations = Object.keys(hierarchy).filter((key) => key !== "Others");
    const preferred = branchKeyFound ? [branchKeyFound] : [];
    const remaining = allFormations
      .filter((key) => !preferred.includes(key))
      .sort((a, b) => a.localeCompare(b));
    return [...preferred, ...remaining];
  }, [branchKeyFound]);

  const clerkDeskOptions = useMemo(() => {
    if (!selectedClerkFormation || !hierarchy[selectedClerkFormation]?.subBranches) return [];

    const subBranchesObj = hierarchy[selectedClerkFormation].subBranches;
    const keys = collectClerkDeskKeys(subBranchesObj);
    const preferredCandidates = [subBranchKeyRaw, branchKeyRaw, unitBranch, subBranch, branch].filter(Boolean);

    const preferred = Array.from(
      new Set(
        preferredCandidates
          .map((candidate) => keys.find((key) => normalizeLoose(key) === normalizeLoose(candidate)))
          .filter(Boolean)
      )
    );

    const remaining = keys
      .filter((key) => !preferred.includes(key))
      .sort((a, b) => a.localeCompare(b));

    return [...preferred, ...remaining];
  }, [
    selectedClerkFormation,
    branchKeyRaw,
    subBranchKeyRaw,
    unitBranch,
    branch,
    subBranch,
    collectClerkDeskKeys,
    normalizeLoose,
  ]);

  const selectedClerkRecipients = useMemo(() => {
    if (!selectedClerkFormation || !selectedClerkUnit) return [];
    // H1.2: authoritative clerk desks come from the DB routing service cache
    // (fetched per formation above); the hierarchy-derived list is only the
    // display fallback while the DB fetch is in flight. The backend
    // re-validates every destination on submission regardless.
    const dbDesks = getClerkRecipients(selectedClerkFormation, selectedClerkUnit, "");
    if (dbDesks.length > 0) return dbDesks;
    const selectedNode = findNestedHierarchyNode(
      hierarchy?.[selectedClerkFormation]?.subBranches,
      selectedClerkUnit
    );
    return getClerkRecipientsFromNode(selectedNode, selectedClerkUnit);
  }, [
    selectedClerkFormation,
    selectedClerkUnit,
    findNestedHierarchyNode,
    getClerkRecipientsFromNode,
    allClerkNetworkRecipientsVersion,
  ]);

  const fileCardRecipientOptions = useMemo(
    () => filterClerkRecipientOptions(authorities).sort(),
    [authorities]
  );

  const selectedClerkDestinationScope = useMemo(() => {
    if (!selectedClerkFormation || !selectedClerkUnit) return null;
    const destinationPath = findHierarchyPath(
      hierarchy?.[selectedClerkFormation]?.subBranches,
      selectedClerkUnit
    ) || [selectedClerkUnit];
    return resolveHierarchyDestination({
      unit: user?.unit || "KA",
      formation: selectedClerkFormation,
      branch: destinationPath[0] || selectedClerkUnit,
      subBranch: destinationPath[1] || "",
      section: destinationPath[2] || "",
    });
  }, [selectedClerkFormation, selectedClerkUnit, user?.unit]);

  // Deduplicate normalized recipients by the resolved scope sent to the server.
  const makeRouteKey = (recipient, scope) => {
    const payload = toRecipientPayload({ ...(scope || {}), recipient });
    return [
      payload.recipient,
      scope?.routingMode,
      scope?.destinationOfficeId,
      scope?.unit,
      scope?.unitBranch,
      scope?.branch,
      scope?.subBranch,
    ].map((value) => normalizeLoose(value)).join("::");
  };

  const toggleRouteDestination = (recipient, scope, label, checked) => {
    const key = makeRouteKey(recipient, scope);
    setRoute((current) => checked
      ? current.some((entry) => entry.key === key)
        ? current
        : [...current, { key, recipient, scope, label }]
      : current.filter((entry) => entry.key !== key));
  };

  useEffect(() => {
    if (!selectedClerkFormation && clerkNetworkFormations.length > 0) {
      setSelectedClerkFormation(clerkNetworkFormations[0]);
    }
  }, [selectedClerkFormation, clerkNetworkFormations]);

  useEffect(() => {
    if (selectedClerkFormation && !clerkDeskOptions.includes(selectedClerkUnit)) {
      setSelectedClerkUnit(clerkDeskOptions[0] || "");
    }
  }, [selectedClerkFormation, selectedClerkUnit, clerkDeskOptions]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadMessage("");

    if (!filesToUpload.length || !classification || !route.length) {
      const validationMessage = "Please select a file, classification, and at least one destination.";
      setMessage(validationMessage);
      setUploadMessage(validationMessage);
      setUploadMessageType("danger");
      return;
    }

    if (["Secret", "Confidential"].includes(classification) && !filePassword) {
      const validationMessage = "A document password is required for classified documents.";
      setMessage(validationMessage);
      setUploadMessage(validationMessage);
      setUploadMessageType("danger");
      return;
    }

    if (!route.every((entry) => entry?.recipient && entry?.scope?.unit)) {
      const validationMessage = "The selected routing destination is invalid. Please select it again.";
      setMessage(validationMessage);
      setUploadMessage(validationMessage);
      setUploadMessageType("danger");
      return;
    }

    setUploading(true);

    try {
      let successfulDeliveries = 0;
      const failedDeliveries = [];
      for (const file of filesToUpload) {
        for (const destination of route) {
        // Normalize recipient identity and retain the resolved destination scope.
        // The server re-validates each delivery.
        const recipientPayload = toRecipientPayload({
          ...(destination.scope || {}),
          recipient: destination.recipient,
        });
        if (!recipientPayload) throw new Error("Select a valid forwarding recipient.");
        const formData = new FormData();
        formData.append("file", file);
        formData.append("classification", classification);
        formData.append("route", JSON.stringify([recipientPayload.recipient]));
        formData.append("userRole", role || "CLERK");
        if (destination.scope?.routingMode === "CURRENT_OFFICE") {
          const appointment = destination.scope.appointments.find((item) => normalizeLoose(item.name) === normalizeLoose(destination.recipient));
          if (!appointment) throw new Error("Refresh recipients and select again.");
          formData.append("routingMode", "CURRENT_OFFICE");
          formData.append("destinationOfficeId", destination.scope.destinationOfficeId);
          formData.append("routingAssignmentId", destination.scope.routingAssignmentId);
          formData.append("destinationAppointmentId", appointment.id);
        }
        formData.append("destinationUnit", destination.scope?.unit || "");
        formData.append("destinationUnitBranch", destination.scope?.unitBranch || "");
        formData.append("destinationBranch", destination.scope?.branch || "");
        formData.append("destinationSubBranch", destination.scope?.subBranch || "");

        if (filePassword) formData.append("password", filePassword);

        const res = await fetch(`${API_URL}/api/uploads/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const responseText = await res.text();
        let data = {};
        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch {
          data = { message: responseText || `Server returned HTTP ${res.status}.` };
        }

        if (!res.ok) {
          failedDeliveries.push(
            `${file.name} -> ${destination.label}: ${data.message || `HTTP ${res.status}`}`
          );
        } else {
          successfulDeliveries += 1;
          const serverMsg = typeof data?.message === "string" ? data.message : "";
          const successMsg = serverMsg
            ? `✅ ${serverMsg}`
            : `✅ ${file.name} uploaded successfully.`;
          setMessage(successMsg);
        }
      }
      }

      const destinationLabels = [...new Set(route.map((entry) => entry.label).filter(Boolean))];
      let successSummary = "File sent successfully.";
      if (filesToUpload.length === 1 && destinationLabels.length === 1) {
        successSummary = `File successfully sent to ${destinationLabels[0]}.`;
      } else if (filesToUpload.length > 1 && destinationLabels.length === 1) {
        successSummary = `${filesToUpload.length} files successfully sent to ${destinationLabels[0]}.`;
      } else if (destinationLabels.length > 1) {
        successSummary = `${filesToUpload.length === 1 ? "File" : `${filesToUpload.length} files`} successfully sent to ${destinationLabels.length} recipients: ${destinationLabels.join(", ")}.`;
      }
      const finalMessage = failedDeliveries.length
        ? `${successfulDeliveries} delivered; ${failedDeliveries.length} failed. ${failedDeliveries.join(" | ")}`
        : successSummary;
      setMessage(finalMessage);
      setUploadMessage(finalMessage);
      setUploadMessageType(failedDeliveries.length ? "danger" : "success");

      // Keep the complete form on failure so the clerk can read the error and
      // retry without selecting the file and destination again.
      if (failedDeliveries.length === 0) {
        setFilesToUpload([]);
        setClassification("");
        setFilePassword("");
        setRoute([]);
        setSelectedClerkUnit("");
        const fileInput = document.querySelector('input[type="file"][name="files"]');
        if (fileInput) fileInput.value = null;
      }

      if (successfulDeliveries > 0) {
        await fetchSentFiles(username, 1, 200);
      }
    } catch (err) {
      console.error("Upload error:", err);
      const failureMessage = `Upload failed: ${err?.message || "Unable to reach the server."}`;
      setMessage(failureMessage);
      setUploadMessage(failureMessage);
      setUploadMessageType("danger");
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = async (file) => {
    if (!token) return alert("You must be logged in to view files.");

    try {
      const isAttachment = file?.parentFileId && file?.id && !file?._id;

      if (isAttachment) {
        const attachmentURL = `${API_URL}/api/uploads/files/${file.parentFileId}/attachments/${file.id}/download`;
        const response = await fetch(attachmentURL, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Unable to view attachment.");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        return;
      }

      if (!file?._id) return alert("Error: File ID is missing or invalid.");

      const lc = file.classification?.toLowerCase();
      if (["confidential", "secret"].includes(lc)) {
        setCurrentFile(file);
        setShowPasswordPopup(true);
        setPasswordPrompt("");
        setPasswordError("");
        return;
      }

      const fileURL = `${API_URL}/api/uploads/files/download/${file._id}`;
      const response = await fetch(fileURL, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Unable to view file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("View file error:", err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleUnlockFile = async () => {
    if (!passwordPrompt) {
      setPasswordError("Password is required.");
      return;
    }

    try {
      // Secure flow: verify the password via POST /access, then download with
      // the short-lived accessToken — the password never travels in a URL.
      const blob = await fetchFileBlob({
        fileId: currentFile._id,
        password: passwordPrompt,
        token,
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setShowPasswordPopup(false);
      setPasswordPrompt("");
      setPasswordError("");
    } catch (err) {
      console.error("Unlock error:", err);
      setPasswordError(err.message || "Error unlocking file. Please try again.");
    }
  };

  const exportToExcel = () => {
    const dataset =
      view === "inbox"
        ? inboxFiles
        : view === "archive"
        ? filteredArchiveFiles
        : filteredSentFiles;

    const sheetRows = (dataset || []).map((file) => ({
      FileName: getDisplayFileName(file),
      Status: file.status,
      Classification: file.classification,
      UploadedBy: getUploadedByRole(file),
      UploadedAt: formatTimestamp(file.createdAt),
      Recipient: file.currentLevel,
    }));

    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Files");
    XLSX.writeFile(wb, `ClerkDashboard_${view}.xlsx`);
  };

  const downloadSelected = async () => {
    const dataset = files; // only allow downloads from Sent list for now

    if (selectedFiles.length === 0) {
      setMessage("No files selected for download.");
      return;
    }

    setDownloading(true);
    setMessage("");

    for (const id of selectedFiles) {
      const file = dataset.find((f) => f._id === id);
      if (!file) continue;

      let password = "";
      if (["Confidential", "Secret"].includes(file.classification)) {
        password = prompt(`Enter password to access ${file.classification} file: ${getDisplayFileName(file)}`);
        if (!password) {
          setMessage((prev) => `${prev}\nSkipped: Password required for ${getDisplayFileName(file)}`);
          continue;
        }
      }

      try {
        // Secure flow: verify the password via POST /access first, then
        // download with the short-lived accessToken (no password in the URL).
        const blob = await fetchFileBlob({ fileId: id, password, token });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = getDisplayFileName(file) || "downloaded_file";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        setMessage((prev) => `${prev}\nError downloading: ${getDisplayFileName(file)}${err?.message ? ` - ${err.message}` : ""}`);
      }
    }

    setDownloading(false);
  };

  // ===================== FILTERS =====================
  const isArchivedOrActioned = useCallback((file) => {
    const status = String(file?.status || "").trim().toLowerCase();
    if (status === "archived" || ACTIONED_STATUS_SET.has(status)) return true;

    const history = Array.isArray(file?.history) ? file.history : [];
    return history.some((h) => ACTIONED_HISTORY_SET.has(String(h?.action || "").trim().toLowerCase()));
  }, []);

  const getApprovedArchivedBy = useCallback((file) => {
    const approvedRole = String(file?.approvedBy?.role || "").trim();
    if (approvedRole) return approvedRole;

    const history = Array.isArray(file?.history) ? file.history : [];
    const preferredActions = new Set(["archive", "approve", "review", "actioned", "reviewed", "finalapproved"]);

    // Prefer reviewer role from approval/archive actions only.
    for (let i = history.length - 1; i >= 0; i -= 1) {
      const action = String(history[i]?.action || "").trim().toLowerCase();
      const reviewer = String(history[i]?.reviewedByRole || history[i]?.reviewedBy || "").trim();
      if (reviewer && preferredActions.has(action)) return reviewer;
    }

    // Fallback: any non-empty reviewedBy in history.
    for (let i = history.length - 1; i >= 0; i -= 1) {
      const reviewer = String(history[i]?.reviewedByRole || history[i]?.reviewedBy || "").trim();
      if (reviewer) return reviewer;
    }

    return "Unknown Role";
  }, []);

  const filteredArchiveFiles = useMemo(() => {
    const term = archiveSearchTerm.toLowerCase();
    return archiveFiles
      .filter((file) => isArchivedOrActioned(file))
      .filter((file) => {
        const name = getDisplayFileName(file).toLowerCase();
        return name.includes(term);
      });
  }, [archiveFiles, archiveSearchTerm, isArchivedOrActioned]);

  const filteredSentFiles = useMemo(() => {
    const result = files
      .filter((file) =>
        getSearchValue(file, searchBy).toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [files, searchBy, searchTerm]);

  const handleFileSelect = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const clearSearch = () => setSearchTerm("");
  const currentViewLabel =
    view === "inbox" ? "Inbox" : view === "sent" ? "Sent" : "History / Archive";
  const contextLabel = [branch || "N/A", subBranch || "N/A"].join(" / ");

  // ===================== RENDER =====================
  return (
    <div className={`wvd master-clerk-dashboard clerk-theme-${clerkTheme}`}>
      {/* ---- Page header: operational clerk desk context ---- */}
      <PageHeader
        eyebrow="Wavey clerk operations"
        title={clerkTitle}
        subtitle={[contextLabel, `View: ${currentViewLabel}`, role || "CLERK"].filter(Boolean).join(" · ")}
      />

      {/* ---- Operational summary (existing data only) ---- */}
      <DashboardSummary
        items={[
          {
            key: "inbox",
            label: "Inbox",
            value: inboxFiles.length,
            context: "Received for action",
            tone: "warning",
            icon: DashboardSummary.icons.inbox,
          },
          {
            key: "sent",
            label: "Sent",
            value: files.length,
            context: "Submitted by you",
            tone: "success",
            icon: DashboardSummary.icons.sent,
          },
          {
            key: "selected",
            label: "Selected",
            value: selectedFiles.length,
            context: "For download/export",
            tone: "info",
            icon: DashboardSummary.icons.clock,
          },
          {
            key: "archive",
            label: "History",
            value: filteredArchiveFiles.length,
            context: "Archived records",
            tone: "neutral",
            icon: DashboardSummary.icons.archive,
          },
        ]}
      />

      {/* ---- Primary work navigation (shared tabs) ---- */}
      <DashboardViewTabs
        activeId={view}
        onChange={(id) => setView(id)}
        items={[
          { id: "inbox", label: "Inbox", icon: Inbox },
          { id: "sent", label: "Sent", icon: Send },
          { id: "archive", label: "History / Archive", icon: Archive },
        ]}
      />

      <div className="wvd-toolbar" role="toolbar" aria-label="Clerk actions">
        <div className="wvd-toolbar__group">
          <WavyButton variant="primary" size="sm" onClick={() => setIsCreateDocMenuOpen(true)}>
            + Create Document
          </WavyButton>
          {view === "sent" ? (
            <WavyButton variant="secondary" size="sm" onClick={exportToExcel}>
              Export
            </WavyButton>
          ) : null}
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <div
          className={`wvd-message ${/(failed|error|invalid|nothing was sent|❌)/i.test(message) ? "wvd-message--error" : /(success|delivered|created|✅)/i.test(message) ? "wvd-message--success" : ""}`.trim()}
          role={/(failed|error|invalid|❌)/i.test(message) ? "alert" : "status"}
          aria-live="polite"
        >
          {message}
        </div>
      )}

        {/* ===================== INBOX VIEW ===================== */}
        {view === "inbox" && (
          <TableShell
            head={
              <tr>
                <th>File</th>
                <th>From (Role)</th>
                <th>Classification</th>
                <th>Status</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            }
          >
            {loadingInbox ? (
              <tr>
                <td colSpan="6">
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--wv-space-2)", padding: "var(--wv-space-3)" }}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="text" style={{ height: 28 }} />
                    ))}
                  </div>
                </td>
              </tr>
            ) : inboxFiles.length === 0 ? (
              <tr>
                <td colSpan="6">
                  <EmptyState title="Inbox clear" hint="No documents currently routed to this desk." />
                </td>
              </tr>
            ) : (
              inboxFiles.map((f) => (
                <tr key={f._id}>
                  <td>{getDisplayFileName(f)}</td>
                  <td>{getUploadedByRole(f)}</td>
                  <td>{f.classification || "-"}</td>
                  <td><StatusBadge status={f.status} /></td>
                  <td>{formatTimestamp(f.updatedAt || f.createdAt)}</td>
                  <td className="gap-2 d-flex">
                    <WavyButton variant="secondary" size="sm" onClick={() => handleViewFile(f)}>
                      View
                    </WavyButton>
                    <WavyButton
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedFileForModal(f);
                        setShowFileModal(true);
                      }}
                    >
                      Details / Forward
                    </WavyButton>
                  </td>
                </tr>
              ))
            )}
          </TableShell>
        )}

        {/* ===================== SENT VIEW ===================== */}
        {view === "sent" && (
          <>
            {/* Upload Form */}
            <form onSubmit={handleUpload} className="wv-card">
              <div className="wv-card__body">
                <FormField label="Files" id="mc-upload-files">
                  <Input
                    type="file"
                    name="files"
                    multiple
                    onChange={(e) => setFilesToUpload([...e.target.files])}
                  />
                </FormField>

                {filesToUpload.length > 0 && (
                  <div className="file-list-preview" style={{ margin: "0 0 var(--wv-space-4)" }}>
                    <strong>Selected Files:</strong>
                    <ul>
                      {filesToUpload.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <FormField label="Classification" id="mc-upload-classification" required>
                  <Select
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                  >
                    <option value="">Select Classification</option>
                    {classifications.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </Select>
                </FormField>

                {(classification === "Secret" || classification === "Confidential") && (
                  <FormField
                    label="Document Password"
                    id="filePassword"
                    required
                    hint="Password will be required to view or download this classified document."
                  >
                    <Input
                      type="password"
                      placeholder="Enter a password for classified document"
                      value={filePassword}
                      onChange={(e) => setFilePassword(e.target.value)}
                      required
                    />
                  </FormField>
                )}

              {/* Normal recipients */}
              <div style={{ margin: "0 0 var(--wv-space-4)" }}>
                <label className="wv-field__label">Forward To:</label>
                <WavyButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRecipients((prev) => !prev)}
                  aria-expanded={showRecipients}
                >
                  {showRecipients ? "Hide Recipients" : "Select Recipients"}
                </WavyButton>

                {!isSOI && <button type="button" className="btn btn-outline-secondary btn-sm" disabled={authoritiesLoading} onClick={() => setAuthoritiesAttempt((value) => value + 1)}>Refresh recipients</button>}
                {showRecipients && authoritiesLoading && <p role="status">Loading recipients...</p>}
                {showRecipients && authoritiesError && <div role="alert">{authoritiesError} <button type="button" onClick={() => setAuthoritiesAttempt((value) => value + 1)}>Retry recipients</button></div>}
                {showRecipients && !authoritiesLoading && !authoritiesError && authorities.length === 0 && <p>No appointments are configured for your current sub-branch.</p>}
                {showRecipients && !authoritiesLoading && !authoritiesError && (
                  <div className="checkbox-group" style={{ marginTop: "var(--wv-space-2)" }}>
                    {authorities.map((auth) => (
                      <div key={auth} className="form-check">
                        <input
                          type="checkbox"
                          name={`upload-recipient-${auth}`}
                          id={`auth-${auth}`}
                          className="form-check-input"
                          checked={route.some((entry) => entry.key === makeRouteKey(auth, currentOfficeScope))}
                          disabled={!currentOfficeScope}
                          onChange={(e) => {
                            if (currentOfficeScope) toggleRouteDestination(auth, currentOfficeScope, `${auth} - ${officeRoster?.officeName || "current unit"}`, e.target.checked);
                          }}
                        />
                        <label htmlFor={`auth-${auth}`} className="form-check-label">
                          {auth}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Clerk Network (ladder) */}
              <div style={{ margin: "0 0 var(--wv-space-4)" }}>
                <label className="wv-field__label">Forward To (Clerk Network):</label>
                <small className="wv-field__hint" style={{ display: "block", marginBottom: "var(--wv-space-2)" }}>
                  Start from the formation or brigade, then choose the unit or desk below it.
                </small>

                <FormField label="Formation / Brigade" id="mc-clerk-formation">
                  <Select
                    value={selectedClerkFormation}
                    onChange={(e) => {
                      setSelectedClerkFormation(e.target.value);
                      setSelectedClerkUnit("");
                    }}
                  >
                    <option value="">Select Formation / Brigade</option>
                    {clerkNetworkFormations.map((formation) => (
                      <option key={formation} value={formation}>
                        {formation}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField
                  label="Unit / Clerk Desk"
                  id="mc-clerk-unit"
                  hint={!selectedClerkUnit && clerkDeskOptions.length > 0 ? `Suggested first: ${clerkDeskOptions[0]}` : undefined}
                >
                  <Select
                    value={selectedClerkUnit}
                    onChange={(e) => setSelectedClerkUnit(e.target.value)}
                    disabled={!selectedClerkFormation}
                  >
                    <option value="">Select Unit / Clerk Desk</option>
                    {clerkDeskOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Select>
                </FormField>

                {selectedClerkUnit && selectedClerkRecipients.length > 0 && (
                  <div className="checkbox-group">
                    {selectedClerkRecipients.map((clerk) => (
                      <div key={clerk} className="form-check">
                        <input
                          type="checkbox"
                          name={`upload-recipient-${selectedClerkUnit}-${clerk}`}
                          id={`clerk-${selectedClerkUnit}-${clerk}`}
                          className="form-check-input"
                          checked={Boolean(selectedClerkDestinationScope) && route.some(
                            (entry) => entry.key === makeRouteKey(clerk, selectedClerkDestinationScope)
                          )}
                          onChange={(e) => {
                            if (selectedClerkDestinationScope) {
                              toggleRouteDestination(
                                clerk,
                                selectedClerkDestinationScope,
                                `${clerk} - ${selectedClerkUnit} - ${selectedClerkFormation}`,
                                e.target.checked
                              );
                            }
                          }}
                        />
                        <label
                          htmlFor={`clerk-${selectedClerkUnit}-${clerk}`}
                          className="form-check-label"
                        >
                          {clerk}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {selectedClerkUnit && selectedClerkRecipients.length === 0 && (
                  <small className="text-muted">
                    No clerk-network recipients found under this desk yet.
                  </small>
                )}
              </div>

              {route.length > 0 && (
                <div className="wvd-message" role="status">
                  <strong>Selected destinations ({route.length})</strong>
                  <ul className="mb-0 mt-2" style={{ margin: "var(--wv-space-2) 0 0", paddingLeft: "1.2rem" }}>
                    {route.map((entry) => (
                      <li key={entry.key} style={{ display: "flex", alignItems: "center", gap: "var(--wv-space-2)", marginBottom: 4 }}>
                        {entry.label}
                        <button
                          type="button"
                          className="wv-link"
                          onClick={() => setRoute((current) => current.filter((item) => item.key !== entry.key))}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploadMessage && (
                <div
                  className={`wvd-message ${uploadMessageType === "danger" ? "wvd-message--error" : uploadMessageType === "success" ? "wvd-message--success" : ""}`.trim()}
                  role="alert"
                  aria-live="assertive"
                  style={{ margin: "0 0 var(--wv-space-3)" }}
                >
                  {uploadMessage}
                </div>
              )}

              <WavyButton type="submit" variant="primary" loading={uploading} disabled={uploading}>
                {uploading ? "Uploading…" : "Upload"}
              </WavyButton>
              </div>
            </form>

            {/* Sent Table */}
            <div className="wv-card" style={{ marginTop: "var(--wv-space-4)" }}>
              <div className="wv-card__header">
                <h3 className="wv-card__title">Sent Files ({filteredSentFiles.length})</h3>
              </div>

              <div style={{ padding: "var(--wv-space-3)", display: "flex", flexDirection: "column", gap: "var(--wv-space-2)", alignItems: "stretch" }}>
                <div className="wvd-filter-row">
                  <FormField label="Search by" id="mc-search-by">
                    <Select value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
                      {searchOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label="Search term" id="mc-search-term">
                    <Input
                      type="text"
                      placeholder={`Search by ${
                        searchOptions.find((opt) => opt.value === searchBy)?.label
                      }...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormField>

                  <WavyButton variant="ghost" size="md" onClick={clearSearch} style={{ height: "var(--wv-control-md)" }}>
                    Clear
                  </WavyButton>
                </div>

                {searchTerm && (
                  <small className="wv-field__hint">
                    Showing {filteredSentFiles.length} of {files.length} sent files
                  </small>
                )}
              </div>

              {loading ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--wv-space-2)", padding: "var(--wv-space-3)" }}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="text" style={{ height: 28 }} />
                  ))}
                </div>
              ) : (
                <TableShell
                  head={
                    <tr>
                      <th>Select</th>
                      <th>File Name</th>
                      <th>Status</th>
                      <th>Classification</th>
                      <th>Uploaded By (Role)</th>
                      <th>Uploaded At</th>
                      <th>Recipient(s)</th>
                      <th>Details</th>
                    </tr>
                  }
                >
                  {filteredSentFiles.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        <EmptyState title="No sent files" hint="Documents you upload will appear here." />
                      </td>
                    </tr>
                  ) : (
                    filteredSentFiles.map((file) => (
                      <tr key={file._id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${getDisplayFileName(file)}`}
                            checked={selectedFiles.includes(file._id)}
                            onChange={() => handleFileSelect(file._id)}
                          />
                        </td>
                        <td>{getDisplayFileName(file)}</td>
                        <td><StatusBadge status={file.status} /></td>
                        <td>{file.classification}</td>
                        <td>{getUploadedByRole(file)}</td>
                        <td>{formatTimestamp(file.createdAt)}</td>
                        <td>
                          {Array.isArray(file.recipients)
                            ? file.recipients.join(", ")
                            : file.currentLevel || "-"}
                        </td>
                        <td>
                          <WavyButton
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedFileForModal(file);
                              setShowFileModal(true);
                            }}
                          >
                            View Details
                          </WavyButton>
                        </td>
                      </tr>
                    ))
                  )}
                </TableShell>
              )}

              <div className="wvd-toolbar" style={{ padding: "var(--wv-space-3)" }}>
                <div className="wvd-toolbar__group">
                  <WavyButton
                    variant="secondary"
                    size="sm"
                    onClick={downloadSelected}
                    disabled={downloading || selectedFiles.length === 0}
                  >
                    {downloading ? "Downloading…" : `Download Selected (${selectedFiles.length})`}
                  </WavyButton>

                  <WavyButton variant="ghost" size="sm" onClick={() => setShowSummary((s) => !s)} aria-pressed={showSummary}>
                    {showSummary ? "Hide Summary" : "Show Summary"}
                  </WavyButton>
                </div>
              </div>

              {showSummary && (
                <div className="wv-card" style={{ margin: "0 var(--wv-space-3) var(--wv-space-3)" }}>
                  <div className="wv-card__header">
                    <h3 className="wv-card__title">Sent files summary</h3>
                  </div>
                  <div className="wv-card__body">
                    <div className="wvd-summary">
                      {[
                        { key: "approved", label: "Approved / Final", value: files.filter((f) => ["Approved", "FinalApproved"].includes(f.status)).length, tone: "success" },
                        { key: "rejected", label: "Rejected", value: files.filter((f) => f.status === "Rejected").length, tone: "danger" },
                        { key: "pending", label: "Pending", value: files.filter((f) => f.status === "Pending").length, tone: "warning" },
                      ].map((stat) => (
                        <div key={stat.key} className={`wvd-summary__card wvd-summary__card--${stat.tone}`}>
                          <span className="wvd-summary__meta">
                            <span className="wvd-summary__label">{stat.label}</span>
                            <span className="wvd-summary__value">{stat.value}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ===================== ARCHIVE VIEW ===================== */}
        {view === "archive" && (
          <div className="wv-card" style={{ margin: "0" }}>
            <div className="wv-card__header">
              <h3 className="wv-card__title">My History / Archived Files</h3>
            </div>

            <div style={{ padding: "var(--wv-space-3)" }}>
              <Input
                type="text"
                aria-label="Search archived files by name"
                placeholder="Search file name..."
                value={archiveSearchTerm}
                onChange={(e) => setArchiveSearchTerm(e.target.value)}
              />
            </div>

            <TableShell
              head={
                <tr>
                  <th>File Name</th>
                  <th>Status</th>
                  <th>Classification</th>
                  <th>Uploaded By (Role)</th>
                  <th>Approved/Archived By (Role)</th>
                  <th>Created</th>
                  <th>Details</th>
                </tr>
              }
            >
              {filteredArchiveFiles.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <EmptyState
                      title={archiveSearchTerm ? "No matching files" : "No archived files"}
                      hint={archiveSearchTerm ? "Try a different search term." : "Documents you archive will appear here."}
                    />
                  </td>
                </tr>
              ) : (
                filteredArchiveFiles.map((file) => (
                  <tr key={file._id}>
                    <td>{getDisplayFileName(file)}</td>
                    <td><StatusBadge status={file.status} /></td>
                    <td>{file.classification}</td>
                    <td>{getUploadedByRole(file)}</td>
                    <td>{getApprovedArchivedBy(file)}</td>
                    <td>{formatTimestamp(file.createdAt)}</td>
                    <td>
                      <WavyButton
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedFileForModal(file);
                          setShowFileModal(true);
                        }}
                      >
                        View Details
                      </WavyButton>
                    </td>
                  </tr>
                ))
              )}
            </TableShell>

            {archiveTotalPages > 1 && (
              <div className="wv-pagination">
                <span className="wv-pagination__info">Page {archivePage} of {archiveTotalPages}</span>
                <div className="wv-pagination__controls">
                  <WavyButton
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const next = Math.max(1, archivePage - 1);
                      setArchivePage(next);
                      fetchArchivedFiles(username, next, 10);
                    }}
                    disabled={archivePage === 1}
                  >
                    <ChevronLeft size={15} aria-hidden="true" /> Previous
                  </WavyButton>
                  <WavyButton
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const next = Math.min(archiveTotalPages, archivePage + 1);
                      setArchivePage(next);
                      fetchArchivedFiles(username, next, 10);
                    }}
                    disabled={archivePage === archiveTotalPages}
                  >
                    Next <ChevronRight size={15} aria-hidden="true" />
                  </WavyButton>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===================== FILECARD MODAL (FORWARD/DETAILS) ===================== */}
        {showFileModal && selectedFileForModal && (
          <Modal
            open
            size="lg"
            title={getDisplayFileName(selectedFileForModal)}
            onClose={() => {
              setShowFileModal(false);
              setSelectedFileForModal(null);
            }}
            footer={
              <>
                <WavyButton
                  variant="secondary"
                  size="sm"
                  onClick={() => document.getElementById(`forward-controls-${selectedFileForModal?._id || "file"}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  Forward File
                </WavyButton>
                <WavyButton
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setShowFileModal(false);
                    setSelectedFileForModal(null);
                  }}
                >
                  Close
                </WavyButton>
              </>
            }
          >
            <FileCard
              file={selectedFileForModal}
              isSummaryView={false}
              userSignature={null}
              role={role || "CLERK"}
              token={token}
              recipientOptions={fileCardRecipientOptions}
              restrictToCurrentOffice={!isSOI}
              currentOfficeScope={currentOfficeScope}
              processing={processingFileId === selectedFileForModal?._id}
              onAction={handleInboxFileAction}
            />
          </Modal>
        )}

        {/* ===================== PASSWORD POPUP MODAL ===================== */}
        {showPasswordPopup && currentFile && (
          <Modal
            open
            size="sm"
            title="Enter password to view"
            onClose={() => {
              setShowPasswordPopup(false);
              setPasswordPrompt("");
              setPasswordError("");
            }}
            footer={
              <>
                <WavyButton
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowPasswordPopup(false);
                    setPasswordPrompt("");
                    setPasswordError("");
                  }}
                >
                  Cancel
                </WavyButton>
                <WavyButton variant="primary" size="sm" onClick={handleUnlockFile}>
                  Unlock & View
                </WavyButton>
              </>
            }
          >
            <p style={{ margin: "0 0 var(--wv-space-3)" }}>
              {getDisplayFileName(currentFile)} ({currentFile.classification})
            </p>
            <FormField label="Document password" id="mc-password-prompt" error={passwordError || undefined}>
              <Input
                type="password"
                placeholder="Enter password"
                value={passwordPrompt}
                onChange={(e) => setPasswordPrompt(e.target.value)}
                autoComplete="off"
              />
            </FormField>
          </Modal>
        )}

        {/* ===================== CREATE DOCUMENT MENU ===================== */}
        <CreateDocumentMenu open={isCreateDocMenuOpen} onClose={() => setIsCreateDocMenuOpen(false)} />
    </div>
  );
};

export default MasterClerkDashboard;

